import { getHierarchyNodes } from 'core/API/hierarchy';
import { paginationService } from 'core/services/paginationService';
import { filterSelectableWorkplaceUsers } from 'core/utils/filterVisibleWorkplaceUsers';
import { getCurrentUserHierarchyId } from 'module/onboarding/utils/hierarchyPermissions';
import { WorkplaceUserListScopeOptions } from 'module/settings/settingsGameMasterTabs/GeneralSetting/UserSettings/filterUserSettingsUsers';

export interface EnrichWorkplaceUsersOptions extends WorkplaceUserListScopeOptions {
	/** When false, skips workplace hierarchy scoping (for User Settings fetch). Default true. */
	applyWorkplaceScopeFilter?: boolean;
}

/**
 * Fetches workplace employees from /workplace_employees/users and enriches them
 * with full user data from /users. Returns an array of enriched user objects
 * sorted by lastName ascending.
 *
 * @param workplaceId - workplace identifier (string or number)
 * @returns Promise<any[]> enriched user list sorted by lastName ASC
 */
export const getEnrichedWorkplaceUsers = async (
	workplaceId: string | number,
	options: EnrichWorkplaceUsersOptions = {}
): Promise<any[]> => {
	const {
		applyWorkplaceScopeFilter = true,
		includeAboveWorkplaceUsers = false,
		currentUserHierarchyId: providedHierarchyId,
	} = options;
	// 1. Fetch workplace employee list (basic fields: id, firstName, lastName, email, phone, middleName)
	const wpData = await paginationService.getWithHandler(
		`workplace_employees/users?workplace=${workplaceId}`,
		undefined,
		100
	);
	const wpUsers: any[] = wpData?.member || wpData?.['hydra:member'] || [];

	if (wpUsers.length === 0) return [];

	// 2. Fetch full user data scoped to workplace when possible
	const fullData = await paginationService.getWithHandler(
		`users?workplace=${workplaceId}`,
		undefined,
		100
	);
	const fullUsers: any[] = fullData?.member || fullData?.['hydra:member'] || [];

	// 3. Build a map of full user details keyed by id
	const fullUsersMap = new Map<number, any>();
	for (const user of fullUsers) {
		const uid = typeof user.id === 'number' ? user.id : Number(user.id);
		if (!Number.isNaN(uid)) {
			fullUsersMap.set(uid, user);
		}
	}

	// 4. Merge: start with workplace employee, overlay with full user data
	const enriched = wpUsers.map((wpUser: any) => {
		const uid = typeof wpUser.id === 'number' ? wpUser.id : Number(wpUser.id);
		const full = fullUsersMap.get(uid);
		if (full) {
			return { ...full, ...wpUser, _full: full };
		}
		return wpUser;
	});

	// 5. Apply shared visibility + workplace scope filters for dropdown pickers
	const hierarchyNodes =
		applyWorkplaceScopeFilter ? await getHierarchyNodes({ page: 1, itemsPerPage: 100 }) : [];

	const currentUserHierarchyId =
		includeAboveWorkplaceUsers
			? (providedHierarchyId ?? getCurrentUserHierarchyId())
			: null;

	const visible = filterSelectableWorkplaceUsers(enriched, {
		workplaceId: Number(workplaceId),
		hierarchyNodes,
		applyWorkplaceScopeFilter,
		includeAboveWorkplaceUsers,
		currentUserHierarchyId,
	});

	// 6. Sort by lastName ascending
	visible.sort((a: any, b: any) =>
		(a.lastName || '').localeCompare(b.lastName || '')
	);

	return visible;
};

/** Options for Schedule, To-Do, and Checklist user pickers (User Settings with above-workplace toggle on). */
export const assignmentWorkplaceUserOptions: EnrichWorkplaceUsersOptions = {
	includeAboveWorkplaceUsers: true,
};

/** Alias for getEnrichedWorkplaceUsers — canonical entry point for dropdown user lists. */
export const getSelectableWorkplaceUsers = getEnrichedWorkplaceUsers;
